import { db } from "~/utils/db";
import { defineStore } from "pinia";
import { navigateTo, useRoute, useRouter, useNuxtApp } from "#app";
import { DateTime } from "luxon";

const FORCE_REFRESH_TIMESTAMP = "2025-09-16T22:00:00Z";

// Notfall-Modus: lets a kiosk that is logged out (no valid JWT, WP backend
// unreachable) mint a synthetic view+kitchen JWT locally so it can keep
// showing cached IndexedDB orders + Supabase routes. The hash is SHA-256 of
// the passphrase; only the hash ships in the bundle. After the expiry below,
// the activation link hides itself and the action refuses to run — so the
// patch self-disarms even if a follow-up redeploy is forgotten.
const OFFLINE_MODE_EXPIRES_AT = "2026-05-23T22:00:00Z";
const OFFLINE_PASSPHRASE_SHA256 =
    "3da736c3b36eea0428bb6c4a2ad8565f2494e0be04928ea3dab6280c539a9c7d";
const OFFLINE_USERNAME = "offline-mode";
// Capabilities granted in Notfall-Modus. Re-read from the bundle on every
// checkAuth() for an offline session — that way a hotfix deploy that adds
// caps takes effect after the next auto-update reload, without the client
// having to re-enter the passphrase.
const OFFLINE_CAPABILITIES = {
    view_calendar: true,
    view_kitchen: true,
    view_routeplanning: true,
    edit_routeplanning: true,
    view_prices: true,
};

function base64UrlEncode(input: string): string {
    return btoa(input)
        .replace(/\+/g, "-")
        .replace(/\//g, "_")
        .replace(/=+$/g, "");
}

async function sha256Hex(text: string): Promise<string> {
    const bytes = new TextEncoder().encode(text);
    const hash = await crypto.subtle.digest("SHA-256", bytes);
    return Array.from(new Uint8Array(hash))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
}

const useProd =
    window.location.hostname.includes("app.refueat.de") ||
    window.location.hostname.includes("app.refueat.dev.ensolarado.de");

const defaultState = {
    shops: [
        {
            id: "berlin",
            name: "Berlin",
            url: "refueat.de",
            delivery_origin_address: "Friedhofsweg 1, 12529 Schönefeld",
            delivery_origin_coordinates: {
                lat: 52.398155163265145,
                lng: 13.447352575194538,
            },
        },
        // {
        //     id: "weisenheim",
        //     name: "Weisenheim",
        //     url: "frankfurt.refueat.de",
        //     delivery_origin_address: "Weisenheim am Sand",
        // },
        // {
        //     id: "hagen",
        //     name: "Hagen",
        //     url: "nrw.refueat.de",
        //     delivery_origin_address: "Arndtstraße 20, 58097 Hagen",
        //     delivery_origin_coordinates: {
        //         lat: "51.36144040380867",
        //         lng: "7.479085834594576",
        //     },
        // },
        {
            id: "dev",
            name: "DEV",
            url: "dev.refueat.de",
            delivery_origin_address: "Friedhofsweg 1, 12529 Schönefeld",
            delivery_origin_coordinates: {
                lat: 52.398155163265145,
                lng: 13.447352575194538,
            },
        },
    ],
    curShop: {
        id: "dev",
        name: "DEV",
        url: "dev.refueat.de",
        delivery_origin_address: "Friedhofsweg 1, 12529 Schönefeld",
        delivery_origin_coordinates: {
            lat: 52.398155163265145,
            lng: 13.447352575194538,
        },
    },
    dailyPlannerUrl: useProd
        ? "https://daily-planner.refueat.dev.ensolarado.de"
        : "http://localhost:8091",
    auth: {
        username: "",
        password: "",
        jwts: {},
        showLoginForm: false,
        loading: false,
        error: false,
        errorMessage: "",
        capabilities: {},
    },
    needsAuthentication: false,
    sidebarOpened: true,
    essentials: null,
    essentialsFetched: false,
    essentialsError: false,

    loading: false, // set to false, once ~some~ data can be shown
    processing: false, // set to false, once ~all~ (and new) data can be shown

    // process orderData centrally
    rawOrderData: null,
    orderData: {}, // No longer persisted, acts as in-memory cache
    orderFetchController: null,

    // process productData centrally
    rawProductData: null,
    productData: null,
    productsJsonFileTime: {
        request: null,
        lastProcessed: null,
    },
    productDataProcessed: false,
    productNameTagMap: {},
    equipment: {
        chafing_dish: {
            name: "Chafing-Dish",
        },
        vorlegebesteck: {
            name: "Vorlegebesteck",
        },
        gn_essen: {
            name: "GN Essen",
        },
        gn_beilage: {
            name: "GN Beilage",
        },
        transportbox: {
            name: "Transport-Box",
        },
        warmhaltebox: {
            name: "Warmhalte-Box",
        },
        suppenbehaelter: {
            name: "Suppenbehälter",
        },
        suppenloeffel: {
            name: "Suppenlöffel",
        },
        greifzange: {
            name: "Greifzange",
        },
        pumpkanne_kaffee: {
            name: "Pumpkanne Kaffee",
        },
        pumpkanne_tee: {
            name: "Pumpkanne Tee",
        },
        automat_kaffee: {
            name: "Automat Kaffee",
        },
        automat_tee: {
            name: "Automat Tee",
        },
    },
    lastSeenRefreshTimestamp: null,
    routeMapPreferences: {
        modalX: null,
        modalY: null,
        modalW: null,
        modalH: null,
        showArrows: true,
        showTravelTime: true,
        showIcons: true,
        showLines: true,
    },
};

export const useDefaultStore = defineStore("defaultStore", {
    state: () => JSON.parse(JSON.stringify(defaultState)),
    persist: {
        pick: [
            "curShop",
            "auth.jwts",
            "auth.username",
            "auth.capabilities",
            "essentials",
            // "orderData", // No longer persisting orderData in localStorage
            "productData",
            "productsJsonFileTime.lastProcessed",
            "productNameTagMap",
            "lastSeenRefreshTimestamp",
            "routeMapPreferences",
        ],
    },
    getters: {
        hasAccess(state) {
            return (requiredCapability) => {
                if (!requiredCapability) return true;
                // user is admin, can do everything
                if (state.auth.capabilities?.administrator) return true;
                if (!state.auth.capabilities) return false;

                return !!state.auth.capabilities[requiredCapability];
            };
        },
    },
    actions: {
        async fetchEssentials() {
            if (this.essentialsFetched) return true;

            // Notfall-Modus: skip the WP call entirely — WP is unreachable.
            // Re-use the essentials persisted from a previous online session.
            if (this.auth.username === OFFLINE_USERNAME) {
                if (this.essentials) {
                    this.essentialsFetched = true;
                    return true;
                }
                this.essentialsError = true;
                return false;
            }

            const maxRetries = 3;
            const retryDelay = 1000; // 1 second
            let attempt = 0;

            // Reset error state at the beginning of a fetch cycle.
            this.essentialsError = false;

            const url = `https://${this.curShop.url}/wp-json/refueat/v1/essentials`;

            while (attempt < maxRetries) {
                attempt++;
                try {
                    const response = await fetch(url, {
                        method: "GET",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${
                                this.auth.jwts[this.curShop.url]
                            }`,
                        },
                    });
                    if (!response.ok) {
                        throw new Error(
                            `HTTP error! Status: ${response.status}`,
                        );
                    }
                    const data = await response.json();
                    this.essentials = data;
                    this.essentialsFetched = true;
                    return true; // Success
                } catch (error) {
                    console.error(
                        `Error fetching essentials (attempt ${attempt}/${maxRetries}):`,
                        error,
                    );
                    if (attempt >= maxRetries) {
                        this.essentialsError = true;
                        const { $tracker: tracker } = useNuxtApp();
                        if (tracker) tracker.handleError(error);
                        return false; // All retries failed
                    }
                    // Wait before retrying
                    await new Promise((resolve) =>
                        setTimeout(resolve, retryDelay),
                    );
                }
            }
            return false; // Should not be reached, but for safety
        },
        async doAuth(evt) {
            const route = useRoute();
            const router = useRouter();
            evt.preventDefault();
            if (!this.auth.username?.length || !this.auth.password?.length)
                return;

            const url = `https://${this.curShop.url}/wp-json/jwt-auth/v1/token`;

            try {
                this.auth.loading = true;
                this.auth.error = false;
                this.auth.errorMessage = "";

                // hacky code to not discard the direct /[order].vue page visit
                const isOrder = !!route.params.order;
                const fullPath = route.fullPath;

                const response = await fetch(url, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        username: this.auth.username,
                        password: this.auth.password,
                    }),
                });

                // push if we are on order page land to go to the desired location
                if (isOrder) router.push(fullPath);

                if (!response.ok) {
                    if (response.status == 403)
                        throw new Error(
                            "Zugriff verweigert. Sind Benutzername und Passwort korrekt?",
                        );
                    else
                        throw new Error(
                            `HTTP error! Status: ${response.status}`,
                        );
                }

                const result = await response.json();
                this.auth.jwts[this.curShop.url] = result.token;
                this.auth.password = ""; // dont save password in cleartext
                this.auth.loading = false;
                this.auth.error = false;
                this.auth.errorMessage = "";
                this.checkAuth();
                await this.fetchEssentials();
                this.initNuxtpage();
            } catch (error) {
                this.auth.loading = false;
                this.auth.error = true;
                this.auth.errorMessage = error;
                console.error("Error:", error);
                const { $tracker: tracker } = useNuxtApp();
                if (tracker) tracker.handleError(error);
            }
        },
        async refreshAuth() {
            try {
                if (!this.auth.jwts[this.curShop.url]) {
                    return;
                }
                // Notfall-Modus: synthetic JWT can't be refreshed by WP.
                if (this.auth.username === OFFLINE_USERNAME) {
                    return;
                }

                const url = `https://${this.curShop.url}/wp-json/jwt-auth/v1/token/refresh`;

                const response = await fetch(url, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${this.auth.jwts[this.curShop.url]}`,
                    },
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }

                const result = await response.json();
                this.auth.jwts[this.curShop.url] = result.token;
            } catch (error) {
                this.auth.loading = false;
                console.error("Error:", error);
                this.sidebarOpened = true;
                const { $tracker: tracker } = useNuxtApp();
                if (tracker) tracker.handleError(error);
            }
        },
        clearAuth() {
            delete this.auth.jwts[this.curShop.url];
            this.needsAuthentication = true;
            this.auth.showLoginForm = true;
            this.auth.capabilities = {};
            this.auth.username = "";
            this.sidebarOpened = true;
        },
        // Notfall-Modus: mint a local synthetic JWT so a kiosk that's logged
        // out can keep showing cached IndexedDB orders + Supabase routes while
        // the WP backend is unreachable. Read+kitchen-edit capabilities only.
        // Returns null on success, a German error string on failure.
        async activateOfflineMode(
            passphrase: string,
        ): Promise<string | null> {
            if (Date.now() > Date.parse(OFFLINE_MODE_EXPIRES_AT)) {
                return "Notfall-Modus ist abgelaufen.";
            }
            const hash = await sha256Hex(passphrase || "");
            if (hash !== OFFLINE_PASSPHRASE_SHA256) {
                return "Falsche Notfall-Passphrase.";
            }
            const expEpoch = Math.floor(
                Date.parse(OFFLINE_MODE_EXPIRES_AT) / 1000,
            );
            const capabilities = OFFLINE_CAPABILITIES;
            const header = base64UrlEncode(
                JSON.stringify({ alg: "none", typ: "JWT" }),
            );
            const payload = base64UrlEncode(
                JSON.stringify({
                    iat: Math.floor(Date.now() / 1000),
                    exp: expEpoch,
                    data: {
                        user: {
                            username: OFFLINE_USERNAME,
                            offline: true,
                            capabilities,
                        },
                    },
                }),
            );
            this.auth.jwts[this.curShop.url] = `${header}.${payload}.offline`;
            this.auth.password = "";
            this.auth.error = false;
            this.auth.errorMessage = "";
            // Re-use persisted essentials; mark as fetched so checkAuth's
            // follow-up fetchEssentials() short-circuits cleanly.
            if (this.essentials) this.essentialsFetched = true;
            const ok = this.checkAuth();
            if (!ok) {
                return "Notfall-Modus konnte nicht aktiviert werden.";
            }
            this.initNuxtpage();
            return null;
        },
        isOfflineModeAvailable(): boolean {
            return Date.now() <= Date.parse(OFFLINE_MODE_EXPIRES_AT);
        },
        async backgroundRefreshAuth() {
            if (!this.auth.jwts[this.curShop.url]) {
                return;
            }
            // Notfall-Modus uses a locally-minted JWT — no WP refresh endpoint
            // to talk to. Keep the synthetic token as-is until its baked-in exp.
            if (this.auth.username === OFFLINE_USERNAME) {
                return;
            }

            const maxRetries = 3;
            const baseDelay = 1000;
            const url = `https://${this.curShop.url}/wp-json/jwt-auth/v1/token/refresh`;

            for (let attempt = 0; attempt < maxRetries; attempt++) {
                try {
                    const response = await fetch(url, {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${this.auth.jwts[this.curShop.url]}`,
                        },
                    });

                    if (response.ok) {
                        // Success - update token and capabilities
                        const result = await response.json();
                        this.auth.jwts[this.curShop.url] = result.token;

                        const authData = this.parseJwt(result.token);
                        if (authData?.data?.user) {
                            this.auth.capabilities =
                                authData.data.user.capabilities || {};
                        }

                        // After updating capabilities, check if the user still has access to the current route.
                        const route = useRoute();
                        const path = route.path;

                        if (
                            path.startsWith("/calendar") &&
                            !this.hasAccess("view_calendar")
                        ) {
                            return navigateTo("/");
                        }
                        if (
                            path.startsWith("/kitchen") &&
                            !this.hasAccess("view_kitchen")
                        ) {
                            return navigateTo("/");
                        }
                        if (
                            path.startsWith("/delivery") &&
                            !this.hasAccess("view_routeplanning")
                        ) {
                            return navigateTo("/");
                        }
                        if (
                            path.startsWith("/routeplanning") &&
                            !this.hasAccess("edit_routeplanning")
                        ) {
                            return navigateTo("/");
                        }
                        return; // Success, exit
                    }

                    // Definitive auth failure (401/403) - token is rejected, log out immediately
                    if (response.status === 401 || response.status === 403) {
                        console.error(
                            `Auth refresh rejected (${response.status}) - logging out`,
                        );
                        this.clearAuth();
                        return;
                    }

                    // Temporary error (5xx, etc) - will retry
                    console.warn(
                        `Auth refresh attempt ${attempt + 1}/${maxRetries} failed (${response.status}), retrying...`,
                    );
                } catch (error) {
                    // Network error - will retry
                    console.warn(
                        `Auth refresh attempt ${attempt + 1}/${maxRetries} failed (network error), retrying...`,
                        error,
                    );
                }

                // Wait before retry (exponential backoff), but not after last attempt
                if (attempt < maxRetries - 1) {
                    await new Promise((resolve) =>
                        setTimeout(resolve, baseDelay * Math.pow(2, attempt)),
                    );
                }
            }

            // All retries failed for temporary errors - keep current session
            // User stays logged in with their existing token, will try again on next checkAuth
            console.error(
                "Auth refresh failed after all retries - keeping current session",
            );
            const { $tracker: tracker } = useNuxtApp();
            if (tracker)
                tracker.handleError(
                    new Error("Auth refresh failed after all retries"),
                );
        },
        checkAuth() {
            if (this.lastSeenRefreshTimestamp !== FORCE_REFRESH_TIMESTAMP) {
                this.hardRefresh();
                return; // Stop execution to prevent errors from running with old data
            }
            const curJwt = this.auth.jwts[this.curShop.url];
            if (curJwt) {
                const authData = this.parseJwt(curJwt);
                const nowTimestamp = Math.floor(new Date().getTime() / 1000);
                if (authData && nowTimestamp <= authData.exp) {
                    this.needsAuthentication = false;
                    this.auth.showLoginForm = false;
                    this.auth.username = authData.data.user.username;
                    // Notfall-Modus: always reload caps from the current
                    // bundle so a hotfix that broadens caps takes effect
                    // after auto-update without manual re-activation.
                    this.auth.capabilities =
                        authData.data.user.username === OFFLINE_USERNAME
                            ? OFFLINE_CAPABILITIES
                            : authData.data.user.capabilities || {};
                    this.sidebarOpened = false;
                    this.fetchEssentials();
                    this.backgroundRefreshAuth(); // non-blocking call
                    return true;
                } else {
                    this.needsAuthentication = true;
                    this.auth.showLoginForm = true;
                    this.auth.capabilities = {};
                    this.auth.username = "";
                    this.sidebarOpened = true;
                    return false;
                }
            } else {
                this.needsAuthentication = true;
                this.auth.showLoginForm = true;
                this.auth.capabilities = {};
                this.auth.username = "";
                this.sidebarOpened = true;
                return false;
            }
        },
        parseJwt(token) {
            try {
                // what exactly is this doing? can be replaced with a library?
                const base64Url = token.split(".")[1];
                const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
                const jsonPayload = decodeURIComponent(
                    window
                        .atob(base64)
                        .split("")
                        .map(function (c) {
                            return (
                                "%" +
                                ("00" + c.charCodeAt(0).toString(16)).slice(-2)
                            );
                        })
                        .join(""),
                );

                return JSON.parse(jsonPayload);
            } catch (error) {
                console.error("Could not parse JWT.", error);
                return null;
            }
        },
        initNuxtpage() {
            const calendarStore = useCalendarStore();
            const fsStore = useFunctionsheetStore();
            const route = useRoute();

            // after a doAuth call, we want to call the initing functions
            if (route.name.includes("calendar")) {
                if (route.params.month != calendarStore.curMonth) {
                    calendarStore.curMonth = route.params.month;
                }
                calendarStore.initCalendar();
            }
            if (route.name.includes("kitchen") && route.name.includes("day")) {
                fsStore.page = "day";
                if (!fsStore.initialized.dayview && !this.loading)
                    fsStore.initFunctionsheetDayview();
            } else if (
                route.name.includes("kitchen") &&
                route.name.includes("week")
            ) {
                fsStore.page = "week";
                if (!fsStore.initialized.weekview && !this.loading)
                    fsStore.initFunctionsheetWeekview();
            } else if (
                route.name.includes("kitchen") &&
                route.name.includes("month")
            ) {
                fsStore.page = "month";
                if (!fsStore.initialized.monthview && !this.loading)
                    fsStore.initFunctionsheetMonthview();
            }
        },
        changeLocation(shop) {
            // this.loading ? '' : (store.curShop = shop) // what it said before

            // TODO when other locations work
            this.curShop = shop;
            if (this.checkAuth()) {
                this.initNuxtpage();
            }
        },
        // helper to take care of multiple page fetch
        async fetchAllPages(url, signal) {
            const store = useDefaultStore();

            let curPage = 1;
            let totalPages = null;
            let totalResponse = [];

            do {
                try {
                    const response = await fetch(`${url}&page=${curPage}`, {
                        method: "GET",
                        headers: {
                            Authorization:
                                "Bearer " + store.auth.jwts[store.curShop.url],
                        },
                        signal,
                    });

                    if (!totalPages) {
                        totalPages = parseInt(
                            response.headers.get("x-wp-totalpages"),
                            10,
                        );
                    }
                    const pageData = await response.json();
                    totalResponse = totalResponse.concat(pageData);
                } catch (err) {
                    if (err.name === "AbortError") {
                        console.log("Fetch aborted");
                        break; // Exit the loop if the request was aborted
                    }
                    console.error(err);
                    const { $tracker: tracker } = useNuxtApp();
                    if (tracker) tracker.handleError(err);
                    totalResponse = [];
                    break; // Also break on other errors
                }

                curPage++;
            } while (curPage <= totalPages);

            return totalResponse;
        },

        async fetchOrdersByDateRange(startDate, endDate) {
            const url =
                "https://" +
                this.curShop.url +
                "/wp-json/refueat/v1/orders-by-date?order_date_from=" +
                startDate +
                "&order_date_to=" +
                endDate +
                "&status=wc-processing,wc-completed,wc-unpaid-invoice";

            // We pass null for signal as we don't need to abort this specific fetch,
            // but fetchAllPages requires the argument.
            const response = await this.fetchAllPages(url, null);
            return response;
        },

        // order data stuff
        async fetchOrders() {
            console.log("fetchOrders called");

            // Abort any ongoing fetch before starting a new one.
            if (this.orderFetchController) {
                this.orderFetchController.abort();
            }
            this.orderFetchController = new AbortController();
            const signal = this.orderFetchController.signal;

            // this.loading = true; // old

            const fsStore = useFunctionsheetStore();
            const calendarStore = useCalendarStore();
            const route = useRoute();

            let response;
            if (route.name.includes("kitchen")) {
                if (fsStore.page == "day") {
                    // v1 - new endpoint
                    const url =
                        "https://" +
                        this.curShop.url +
                        "/wp-json/refueat/v1/orders-by-date?order_date=" +
                        fsStore.date +
                        "&status=wc-processing,wc-completed,wc-unpaid-invoice";

                    response = await this.fetchAllPages(url, signal);
                } else {
                    // v1 - new endpoint - inclusive of both start and end date!
                    const url =
                        "https://" +
                        this.curShop.url +
                        "/wp-json/refueat/v1/orders-by-date?order_date_from=" +
                        fsStore.days[0] +
                        "&order_date_to=" +
                        fsStore.days[fsStore.days.length - 1] +
                        "&status=wc-processing,wc-completed,wc-unpaid-invoice";

                    response = await this.fetchAllPages(url, signal);
                }
            } else if (route.name.includes("calendar")) {
                // old: get orders for last 36 (~ last month) and next 66 days (~ this and next month)
                // new: get orders for all shown days in calendar (full weeks only)
                const isoFrom =
                    calendarStore.calendarMonthDays[0].toLocaleDateString("sv");
                const isoTo =
                    calendarStore.calendarMonthDays[
                        calendarStore.calendarMonthDays.length - 1
                    ].toLocaleDateString("sv");

                const url =
                    "https://" +
                    this.curShop.url +
                    "/wp-json/refueat/v1/orders-by-date?order_date_from=" +
                    isoFrom +
                    "&order_date_to=" +
                    isoTo +
                    "&status=wc-processing,wc-completed,wc-unpaid-invoice,wc-angebot,wc-angebotsanfrage";

                response = await this.fetchAllPages(url, signal);
            } else if (
                route.name.includes("routeplanning") ||
                route.name.includes("delivery")
            ) {
                // routeplanning: get orders for the selected date
                const url =
                    "https://" +
                    this.curShop.url +
                    "/wp-json/refueat/v1/orders-by-date?order_date=" +
                    fsStore.date +
                    "&status=wc-processing,wc-completed,wc-unpaid-invoice";

                response = await this.fetchAllPages(url, signal);
            }
            this.rawOrderData = response;
            // console.log("done with fetchOrders");

            // return response;
        },

        async hydrateOrderData(dates: string[]): Promise<number> {
            console.log(`Hydrating order data for ${dates.length} dates.`);
            const orders = await db.orders
                .where("date")
                .anyOf(dates)
                .and((order) => order.shopUrl === this.curShop.url)
                .toArray();

            const ordersByDate = {};
            // Initialize with empty arrays for all requested dates
            for (const day of dates) {
                ordersByDate[day] = [];
            }
            // Populate with data from DB
            for (const order of orders) {
                if (ordersByDate[order.date]) {
                    // This check is a bit redundant now but safe
                    ordersByDate[order.date].push(order);
                }
            }

            // Replace the data for the current shop
            this.orderData[this.curShop.url] = ordersByDate;
            return orders.length;
        },

        async processOrderData() {
            console.log("processOrderData called");

            if (!this.productData) {
                await this.fetchProducts();
                this.processProductData();
            }

            const route = useRoute();
            const fsStore = useFunctionsheetStore();
            const routeStore = useRouteStore();
            const { getDeliveryNameFromOrder } = useOrder();
            const { normalizeAddressString } = useUtil();

            const data = this.rawOrderData;

            // console.log("rawOrderData: " + data);

            if (!data || (typeof data === "object" && !Array.isArray(data))) {
                console.error(
                    "the fetched response did not provide correct data.",
                );
                return;
            }

            const shop = this.curShop;
            const ordersToStoreInDB = [];
            const apiOrderIds = new Set<number>();

            // Loop through fetched orders to prepare them for the database
            for (const order of data) {
                // if (order.data?.status && order.data?.status == 403) continue; // current bugfix, needs to be reviewed

                apiOrderIds.add(order.id);
                const shippingMethod = getDeliveryNameFromOrder(order);

                let date;
                if (shippingMethod == "Lieferung") {
                    date = order.meta_data.find(
                        (m) => m.key == "delivery_date",
                    ).value;
                } else {
                    date = order.meta_data.find(
                        (m) => m.key == "pickup_date",
                    ).value;
                }

                const warenkoerbe = {};
                for (const meta of order.meta_data) {
                    if (meta.key.startsWith("warenkoerbe_")) {
                        const key_match = meta.key.match(
                            /^warenkoerbe_(\d+)_(.*)$/,
                        );
                        const num = key_match[1];
                        const key = key_match[2];
                        if (!warenkoerbe.hasOwnProperty(num))
                            warenkoerbe[num] = {};
                        warenkoerbe[num][key] = meta.value;
                    }
                }

                // If there are sub-carts, process them first.
                if (Object.keys(warenkoerbe).length > 0) {
                    for (const [idx, warenkorb] of Object.entries(
                        warenkoerbe,
                    )) {
                        const additionalEntry = {};
                        this.createOrderEntry(
                            order,
                            additionalEntry,
                            shippingMethod,
                            warenkorb,
                        );
                        ordersToStoreInDB.push({
                            ...additionalEntry,
                            shopUrl: shop.url,
                            date: date,
                        });
                    }
                }

                // Process the main part of the order (or the only part if not split).
                // The main part contains line items that do not have a "warenkorb" meta field.
                const orderEntry = {};
                this.createOrderEntry(order, orderEntry, shippingMethod, null); // Explicitly pass null for warenkorb

                // Only add this part if it actually contains items.
                if (orderEntry.line_items.length > 0) {
                    // NOTE: this code is omitted
                    // we dont use A visually, ever. Because if another warenkob
                    // A is only used as a placeholder for "default"

                    // If the order was split, this main part is designated as 'A'.
                    // if (Object.keys(warenkoerbe).length > 0) {
                    //     if (orderEntry.dailyId) {
                    //         orderEntry.dailyId += "A";
                    //     }
                    // }

                    ordersToStoreInDB.push({
                        ...orderEntry,
                        shopUrl: shop.url,
                        date: date,
                    });
                }
            }

            // Clean up orphaned DB entries before saving new data.
            // This happens if a dailyId changes, leaving the old entry behind.
            if (apiOrderIds.size > 0) {
                // 1. Find all existing DB records for the fetched order IDs for the current shop.
                const existingDBOrders = await db.orders
                    .where("id")
                    .anyOf(...Array.from(apiOrderIds))
                    .and((order) => order.shopUrl === shop.url)
                    .toArray();

                // 2. Create a set of "correct" primary keys from the fresh API data.
                const freshPrimaryKeys = new Set(
                    ordersToStoreInDB.map((o) => `${o.id}-${o.dailyId}`),
                );

                // 3. Identify orphans by checking which existing DB records are not in the fresh data.
                const orphansToDelete = existingDBOrders.filter(
                    (dbOrder) =>
                        !freshPrimaryKeys.has(
                            `${dbOrder.id}-${dbOrder.dailyId}`,
                        ),
                );

                // 4. If orphans are found, delete them.
                if (orphansToDelete.length > 0) {
                    const orphanDexieKeys = orphansToDelete.map((o) => [
                        o.id,
                        o.dailyId,
                    ]);
                    console.log(
                        `Deleting ${orphansToDelete.length} orphaned order parts from IndexedDB.`,
                        orphanDexieKeys,
                    );
                    await db.orders.bulkDelete(orphanDexieKeys);
                }
            }

            // Check for new data by comparing with what's already in the DB
            const dbOrders = await db.orders
                .where("id")
                .anyOf(...Array.from(apiOrderIds))
                .toArray();
            const dbOrderMap = new Map(
                dbOrders.map((o) => [`${o.id}-${o.dailyId}`, o]),
            );

            let newDataProcessed = false;
            if (route.name.includes("routeplanning")) {
                routeStore.addressChanges = []; // clear it before processing
            }

            for (const order of ordersToStoreInDB) {
                const existingOrder = dbOrderMap.get(
                    `${order.id}-${order.dailyId}`,
                );
                if (
                    !existingOrder ||
                    existingOrder.modifiedAt !== order.modifiedAt
                ) {
                    newDataProcessed = true;
                    // do not break, continue to check for address changes
                }

                // Check for address changes for routed orders
                if (
                    route.name.includes("routeplanning") &&
                    routeStore.routePlanningData?.length > 0
                ) {
                    const latestVersion =
                        routeStore.routePlanningData[
                            routeStore.routePlanningData.length - 1
                        ];

                    if (latestVersion?.routes && latestVersion?.deliveries) {
                        const isRouted = latestVersion.routes.some((r) =>
                            r.route.some((stop) => stop.id === order.dailyId),
                        );

                        if (isRouted) {
                            const deliveryInRoute =
                                latestVersion.deliveries.find(
                                    (d) => d.id === order.dailyId,
                                );

                            if (deliveryInRoute) {
                                const routeAddressNormalized =
                                    normalizeAddressString(
                                        deliveryInRoute.address,
                                    );
                                const newAddressNormalized =
                                    normalizeAddressString(
                                        order.shippingAddress,
                                    );

                                if (
                                    routeAddressNormalized !==
                                    newAddressNormalized
                                ) {
                                    // Check if this change is already in addressChanges to avoid duplicates
                                    if (
                                        !routeStore.addressChanges.some(
                                            (c) => c.dailyId === order.dailyId,
                                        )
                                    ) {
                                        routeStore.addressChanges.push({
                                            dailyId: order.dailyId,
                                            customerName: order.customerName,
                                            oldAddress: deliveryInRoute.address,
                                            newAddress: order.shippingAddress,
                                        });
                                    }
                                }
                            }
                        }
                    }
                }
            }

            if (ordersToStoreInDB.length > 0) {
                try {
                    // Dexie cannot store Vue's reactive Proxy objects. We need to convert them to plain
                    // JavaScript objects before storing. JSON.parse(JSON.stringify(...)) is a reliable
                    // way to deep-clone and remove the Proxy wrappers.
                    const plainOrders = JSON.parse(
                        JSON.stringify(ordersToStoreInDB),
                    );
                    await db.orders.bulkPut(plainOrders);
                    console.log(
                        `${plainOrders.length} orders saved to IndexedDB.`,
                    );

                    // After saving to DB, we must update the in-memory state so the UI
                    // can react to the new data, especially after a hard refresh.
                    if (!this.orderData[shop.url]) {
                        this.orderData[shop.url] = {};
                    }
                    const newOrdersByDate = {};
                    for (const order of plainOrders) {
                        const date = order.date;
                        if (!newOrdersByDate[date]) {
                            newOrdersByDate[date] = [];
                        }
                        newOrdersByDate[date].push(order);
                    }

                    // Merge new data into the store's state
                    for (const date in newOrdersByDate) {
                        this.orderData[shop.url][date] = newOrdersByDate[date];
                    }
                } catch (error) {
                    console.error("Failed to save orders to IndexedDB", error);
                    const { $tracker: tracker } = useNuxtApp();
                    if (tracker) tracker.handleError(error);
                }
            }

            const isFS = route.name.includes("kitchen");
            let orderDataWasFullyFilledAlready = false;
            if (isFS) {
                if (fsStore.page == "day") {
                    orderDataWasFullyFilledAlready =
                        !!this.orderData?.[shop.url]?.[fsStore.date];
                } else {
                    orderDataWasFullyFilledAlready = fsStore.days.every(
                        (date) => !!this.orderData?.[shop.url]?.[date],
                    );
                }
            }

            if (newDataProcessed && orderDataWasFullyFilledAlready) {
                fsStore.updatedOrderDataProcessed = true;
            }

            return newDataProcessed;
        },

        createOrderEntry(order, orderEntry, shippingMethod, warenkorb = null) {
            const fsStore = useFunctionsheetStore();
            const calendarStore = useCalendarStore();
            const { getTimeFromOrder } = useOrder();
            const getMetaValue = (key) =>
                order.meta_data.find((item) => item.key === key)?.value;

            const line_items = order.line_items.filter((item) => {
                const item_warenkorb_meta = item.meta_data.find(
                    (m) => m.key == "warenkorb",
                );
                if (!warenkorb) {
                    // default warenkorb (includes items with warenkorb: A or no warenkorb meta)
                    if (item_warenkorb_meta && item_warenkorb_meta.value !== 'A') {
                        // item has warenkorb meta AND it's not 'A' -> belongs to another cart
                        return false;
                    }
                } else {
                    // we have a special warenkorb!
                    if (
                        !item_warenkorb_meta ||
                        item_warenkorb_meta.value != warenkorb.id
                    ) {
                        // case 1: item has no meta noted -> its in "normal" cart -> not in this!
                        // case 2: item has meta, but its another warenkorb -> not in this!
                        return false;
                    }
                }
                return true;
            });

            orderEntry.warenkorbId = warenkorb ? warenkorb.id : null; // only set, if multiple!

            orderEntry.menus = []; // FS specific
            // loop through items within one order
            for (let j = 0; j < line_items.length; j++) {
                const item = line_items[j];
                // Use variation_id if it's a product variation, otherwise use product_id
                // Note: item.id is the line_item ID (unique per order), NOT the product ID
                const product = this.productData?.[item.variation_id || item.product_id];
                this.setupProductQuantitiesOfOrder(item, product, orderEntry);
            }

            // save order information
            orderEntry.id = order.id;
            orderEntry.status = order.status; // calendar specific
            orderEntry.line_items = line_items; // used to check for service/hot/foodbike
            const daily_id = order.meta_data.find(
                (item) => item.key === "daily_id",
            );

            // Workaround:
            // if no daily_id is give by backend (should never happen) use order_id
            // just so we don't lose data for any reason
            orderEntry.dailyId = daily_id?.value ? daily_id.value : order.id;

            orderEntry.amount_people = getMetaValue("amount_people") ?? "";

            orderEntry.phone_delivery =
                getMetaValue("delivery_phone_number") ?? "";

            if (warenkorb && orderEntry.dailyId)
                orderEntry.dailyId += warenkorb.id;

            const s = order.shipping;
            const b = order.billing;
            orderEntry.shippingAddress2 = s.address_2;
            orderEntry.shippingAddress = `${s.address_1}, ${s.postcode} ${s.city}`;
            orderEntry.company = s.company || "";

            orderEntry.shippingLatitude =
                parseFloat(getMetaValue("shipping_latitude")) || null;
            orderEntry.shippingLongitude =
                parseFloat(getMetaValue("shipping_longitude")) || null;

            orderEntry.phone = order.billing.phone || "";
            orderEntry.customerEmail = order.billing.email || "";
            orderEntry.customerName =
                (s.first_name || "") + " " + (s.last_name || "");
            if (orderEntry.customerName == " ")
                orderEntry.customerName =
                    (b.first_name || "") + " " + (b.last_name || ""); // see if name is in billing instead

            let cN = order.customer_note || "";
            if (cN.includes("[:de]")) {
                const regex = /\[:de\](.*?)(?:\[:en\]|\[:\])/s;
                const onlyGermanCN = cN.match(regex);
                if (onlyGermanCN && onlyGermanCN.length > 1)
                    cN = onlyGermanCN[1];
            }
            orderEntry.customerNote = cN;
            orderEntry.isStammkunde = null;

            orderEntry.orderNumber = order.number;

            orderEntry.shippingMethod = shippingMethod;
            orderEntry.paymentMethod = order.payment_method_title;
            orderEntry.total =
                order.total.replace(".", ",") + order.currency_symbol;
            orderEntry.createdAt = order.date_created.date;
            orderEntry.modifiedAt = order.date_modified.date;
            orderEntry.cancelled = false; // maybe fs specific?
            orderEntry.totalValue = Number(order.total);
            orderEntry.totalNetValue =
                Number(order.total) - Number(order.total_tax); // calendar specific
            orderEntry.totalNet =
                orderEntry.totalNetValue.toFixed(2).replace(".", ",") +
                order.currency_symbol; // calendar specific

            // save array of tags that apply from meta
            const fsTags = order.meta_data.find((o) => o.key == "fs_tags");
            if (fsTags) {
                const tags = fsTags.value;

                // denotes, that tags are saved per warenkorb
                if (
                    typeof tags === "object" &&
                    !Array.isArray(tags) &&
                    tags !== null
                ) {
                    const warenkorbId = warenkorb ? warenkorb.id : "A";
                    orderEntry.fsTags = tags[warenkorbId];
                } else {
                    orderEntry.fsTags = tags;
                }
            } else orderEntry.fsTags = [];

            // add tablecards as "fake" fs_tag
            const hasTablecards = getMetaValue("table_cards") == "yes";
            if (hasTablecards) orderEntry.fsTags.push("tablecards");

            if (order.payment_method_title === "Barzahlung") {
                orderEntry.fsTags.push("barzahlung");
            }

            // set boxes for order
            const metaBoxes = order.meta_data.find((o) => o.key == "boxes");
            if (metaBoxes) {
                const boxes = metaBoxes.value;

                // denotes, that boxes are saved per warenkorb
                if (
                    typeof boxes === "object" &&
                    !Array.isArray(boxes) &&
                    boxes !== null
                ) {
                    const warenkorbId = warenkorb ? warenkorb.id : "A";
                    orderEntry.boxes = boxes[warenkorbId];

                    // if(!orderEntry.boxes) orderEntry.boxes = [];
                    orderEntry.packed = !!orderEntry.boxes?.length;
                    const packedBy = order.meta_data.find(
                        (o) => o.key == "boxes_packed_by",
                    );
                    if (
                        typeof packedBy === "object" &&
                        !Array.isArray(packedBy) &&
                        packedBy !== null
                    ) {
                        orderEntry.packedBy = packedBy[warenkorbId]; // can be null
                    } else if (packedBy !== null) {
                        console.error(
                            "packed by is not aligning with boxes for this multiple warenkorb order",
                        );
                    } else orderEntry.packedBy = null;
                } else {
                    // normal warenkorb case
                    orderEntry.boxes = boxes;
                    orderEntry.packed = !!boxes.length;
                    const packedBy = order.meta_data.find(
                        (o) => o.key == "boxes_packed_by",
                    );
                    if (packedBy) orderEntry.packedBy = packedBy.value;
                    else orderEntry.packedBy = null;
                }
            } else {
                orderEntry.boxes = [];
                orderEntry.packed = false;
                orderEntry.packedBy = null;
            }

            // read out google maps info from metas
            if (shippingMethod.includes("Lieferung")) {
                orderEntry.distance = getMetaValue("delivery_distance") ?? "";
                orderEntry.bearing = getMetaValue("delivery_bearing") ?? "";
            } else {
                orderEntry.distance = "";
            }

            // delivery timeframe - usually 120 minutes
            if (!warenkorb) {
                orderEntry.deliveryTimeframe =
                    Number(getMetaValue("delivery_timeframe")) || 120;
            } else {
                orderEntry.deliveryTimeframe =
                    Number(warenkorb.delivery_timeframe) || 120;
            }

            let [timeSlot, fsSlot] = getTimeFromOrder(order, warenkorb);
            const rawDeliveryTime = timeSlot;
            // orderEntry.time = time; // not needed
            // check format of timeSlot - if its old, it contains a "-", then we keep it as is
            // otherwise we calculate the duration taking the delivery timeframe into account!
            if (!timeSlot.includes("-")) {
                const hours = orderEntry.deliveryTimeframe == 120 ? 2 : 1;
                const timeSlotStartHours = Number(timeSlot.slice(0, 2)) - hours;
                const timeSlotStartMinutes = timeSlot.slice(3, 5);
                timeSlot =
                    timeSlotStartHours +
                    ":" +
                    timeSlotStartMinutes +
                    " - " +
                    timeSlot;
            }
            orderEntry.timeSlot = timeSlot;
            orderEntry.fsSlot = fsSlot; // fs specific

            // Mirrors daily-planner's getTimeFromOrder (deliveriesController.js):
            // earliestTime/latestTime are ISO timestamps anchored to the delivery date.
            // Used by routeplanning's checkForUpdates to detect WC-side time changes
            // against the snapshot stored on the supabase route.
            orderEntry.earliestTime = null;
            orderEntry.latestTime = null;
            if (shippingMethod === "Lieferung" && rawDeliveryTime && rawDeliveryTime !== "-") {
                const deliveryDate = getMetaValue("delivery_date");
                let latestTimeStr = rawDeliveryTime;
                if (latestTimeStr.includes("-")) {
                    latestTimeStr = latestTimeStr.split("-").map((s) => s.trim())[1];
                }
                if (deliveryDate && /^\d{1,2}:\d{2}$/.test(latestTimeStr)) {
                    const [hh, mm] = latestTimeStr.split(":");
                    const padded = `${hh.padStart(2, "0")}:${mm}`;
                    const latestDT = DateTime.fromISO(`${deliveryDate}T${padded}`);
                    if (latestDT.isValid) {
                        const earliestDT = latestDT.minus({
                            minutes: orderEntry.deliveryTimeframe,
                        });
                        orderEntry.earliestTime = earliestDT.toISO();
                        orderEntry.latestTime = latestDT.toISO();
                    }
                }
            }

            // calendar specific stuff:
            // directions link
            const formatted = encodeURIComponent(orderEntry.shippingAddress);
            // assumes shop in bautzener str. to be origin!
            orderEntry.directionsLink =
                "https://www.google.com/maps/dir/Friedhofsweg+1,+12529+Schönefeld,+Deutschland/" +
                formatted +
                "/";

            // find out if there are updated versions of this order
            orderEntry.originalAngebotOrderId =
                getMetaValue("original_angebot_order_id") ?? "";
            orderEntry.previousOrderId =
                getMetaValue("previous_order_id") ?? "";

            // init if undefined
            if (!calendarStore.hiddenOrderIds[this.curShop.url])
                calendarStore.hiddenOrderIds[this.curShop.url] = [];

            if (orderEntry.originalAngebotOrderId) {
                calendarStore.hiddenOrderIds[this.curShop.url].push(
                    orderEntry.originalAngebotOrderId,
                );
            } else if (orderEntry.previousOrderId) {
                calendarStore.hiddenOrderIds[this.curShop.url].push(
                    orderEntry.previousOrderId,
                );
            }

            orderEntry.dishes = {
                providedBy: getMetaValue("dishes_provided_by") || "kein",
                externalProvider:
                    getMetaValue("external_dishes_provider") || "",
                externalProviderNumber:
                    getMetaValue("external_dishes_provider_number") || "",
            };
            orderEntry.service = {
                providedBy: getMetaValue("service_provided_by") || "kein",
                externalProvider:
                    getMetaValue("external_service_provider") || "",
                externalProviderNumber:
                    getMetaValue("external_service_provider_number") || "",
            };
            const accessibility = getMetaValue("accessibility") || "";
            orderEntry.notAccessible = accessibility == "Nein" ? true : false;

            const equipmentsMeta = getMetaValue("equipments");
            const equipmentAmounts = {};
            if (equipmentsMeta) {
                const warenkorbId = warenkorb ? warenkorb.id : "A";
                const warenkorbEquipments = equipmentsMeta[warenkorbId];

                if (warenkorbEquipments) {
                    for (const eqSlug in warenkorbEquipments) {
                        if (
                            Object.prototype.hasOwnProperty.call(
                                warenkorbEquipments,
                                eqSlug,
                            )
                        ) {
                            const eqData = warenkorbEquipments[eqSlug];
                            let totalAmount;
                            if (eqData.packed && eqData.packed !== 0) {
                                totalAmount = eqData.packed;
                            } else {
                                totalAmount =
                                    (eqData.calculated || 0) +
                                    (eqData.manual || 0);
                            }

                            if (totalAmount > 0) {
                                equipmentAmounts[eqSlug] = totalAmount;
                            }
                        }
                    }
                }
            }
            orderEntry.hasEquipment = !!Object.keys(equipmentAmounts).length;
            orderEntry.equipmentAmounts = equipmentAmounts;

            // Equipment return/retrieval information
            orderEntry.equipmentReturnDay = getMetaValue("equipment_return_day") || "";
            orderEntry.equipmentReturnedBy = getMetaValue("equipment_returned_by") || "";
            orderEntry.equipmentReturnTimeStart = getMetaValue("equipment_return_time_start") || "";
            orderEntry.equipmentReturnTimeEnd = getMetaValue("equipment_return_time_end") || "";
        },

        // orderEntry helper 1
        setupProductQuantitiesOfOrder(item, product, orderEntry) {
            if (!product) {
                // UNBEKANNT
                // this is the case if we are dealing with very old orders with no longer known products - from like 2023 and older
                orderEntry.menus.push({
                    name: `Prod. ${item.product_id}${
                        item.variation_id ? ` Var. ${item.variation_id}` : ""
                    }`,
                    id: item.variation_id || item.product_id, // give it a unique id
                    qty: item.quantity,
                    size: "",
                });
                return;
            }

            let name = product.fs_infos.product;
            if (product.sku.includes("EXTRA-")) {
                let customName = item.meta_data.find(
                    (m) => (m.key == "name" || m.key == "Name") && !!m.value,
                )?.value;
                if (!customName) customName = "nicht definiert...";
                name = name + ": " + customName;
            }

            // product is already the correct line item
            orderEntry.menus.push({
                name,
                id: product.id,
                qty: item.quantity * product.fs_infos.qty,
                size: product.fs_infos.size, // this might be defined or not
            });
        },

        // product data stuff
        async fetchProducts() {
            // fka: processProducts
            console.log("fetchProducts called");

            const store = useDefaultStore();

            // if this was done already, we dont need to do it again in this session!
            if (this.productDataProcessed) return;

            const url =
                "https://" +
                store.curShop.url +
                "/wp-admin/admin-ajax.php?action=get_products";

            await fetch(url)
                .then((response) => {
                    // read out filetime from products.json to determine, whether we need to update productData later
                    const fileModificationTime = response.headers.get(
                        "x-file-modification-time",
                    );
                    this.productsJsonFileTime.request = fileModificationTime;

                    return response.json();
                })
                .then((response) => {
                    this.rawProductData = response;
                })
                .catch((err) => {
                    console.error(err);
                    const { $tracker: tracker } = useNuxtApp();
                    if (tracker) tracker.handleError(err);
                });
        },

        processProductData() {
            // console.time("processProductData");
            console.log("processProductData called");

            if (!this.rawProductData) return;

            // only setup productData if undefined or file contents are newer than last fetch
            if (
                this.productData != null &&
                this.productsJsonFileTime.request ==
                    this.productsJsonFileTime.lastProcessed &&
                !!Object.keys(this.productNameTagMap).length
            ) {
                console.log("exiting processProductData");
                // console.timeEnd("processProductData");
                return;
            }

            const transformedProducts = Object.entries(
                this.rawProductData,
            ).reduce((acc, [slug, product]) => {
                // Transform main product
                const fs_infos = (() => {
                    if (!product.product_table?.trim()) {
                        return { product: product.name, qty: "1", size: "" };
                    }
                    try {
                        const parsed = JSON.parse(product.product_table);
                        // Validate it's an object with expected properties if needed
                        return typeof parsed === "object" && parsed !== null
                            ? parsed
                            : { product: product.name, qty: "1", size: "" };
                    } catch {
                        return { product: product.name, qty: "1", size: "" };
                    }
                })();
                const transformedProduct = {
                    ...product,
                    fs_infos,
                };
                transformedProduct.product_table = undefined; // Instead of delete

                // Transform variations if they exist
                if (transformedProduct.variations?.length) {
                    transformedProduct.variations =
                        transformedProduct.variations.map((variation) => {
                            const fs_infos = (() => {
                                if (!variation.product_table?.trim()) {
                                    return {
                                        product: variation.name,
                                        qty: "1",
                                        size: "",
                                    };
                                }
                                try {
                                    const parsed = JSON.parse(
                                        variation.product_table,
                                    );
                                    // Validate it's an object with expected properties if needed
                                    return typeof parsed === "object" &&
                                        parsed !== null
                                        ? parsed
                                        : {
                                              product: variation.name,
                                              qty: "1",
                                              size: "",
                                          };
                                } catch {
                                    return {
                                        product: variation.name,
                                        qty: "1",
                                        size: "",
                                    };
                                }
                            })();
                            return {
                                ...variation,
                                fs_infos,
                                product_table: undefined, // Instead of delete
                            };
                        });
                }

                acc[slug] = transformedProduct;
                return acc;
            }, {});

            const products = transformedProducts;

            // restructure the object: ids are keys, variations also appear on toplevel with their id
            const transformed = {};
            Object.entries(products).forEach(([slug, product]) => {
                // Add the main product by its ID
                transformed[product.id] = { ...product, slug };
                // delete transformed[product.id].variations; // Remove variations if present

                // If the product has variations, add each variation by its ID
                // ..and remove parent product from productData (as its never needed)
                if (product.variations) {
                    product.variations.forEach((variation) => {
                        transformed[variation.id] = { ...variation };
                        // add the fs_category from the parent product before deletion
                        transformed[variation.id].fs_category =
                            transformed[product.id].fs_category ||
                            "uncategorized";
                        // add the mindesthaltbarkeit hours from parent as well
                        transformed[variation.id].durability_hours =
                            transformed[product.id].durability_hours;
                        // add fs_tags as well
                        transformed[variation.id].fs_tags =
                            transformed[product.id].fs_tags;
                    });
                    delete transformed[product.id];
                }
            });

            // go over the productData and clean up product data:
            // - remove gutscheine
            // - remove "Catering vom Foodbike", id 5551
            // - remove catering-pakete
            // - remove mixes (condition: multiple entries in fs_infos and "not_standalone" == true), or "MIX" in sku
            Object.entries(transformed).forEach(([id, product]) => {
                if (product.sku.includes("GUTSCHEIN-")) delete transformed[id];
                if (product.sku == "FOODBIKE") delete transformed[id];
                if (product.sku.includes("CAT-")) {
                    if (
                        product.sku.includes("-FLE") ||
                        product.sku.includes("-VEG")
                    )
                        delete transformed[id];
                }
                if (product.sku.includes("-MIX-") && product.not_standalone)
                    delete transformed[id];

                // cast durability_hours to number, default to 8
                product.durability_hours = product?.durability_hours
                    ? Number(product.durability_hours)
                    : 8;
            });

            // we have 300 remaining products, but only 253 unique names for them
            // restructure, such that each name is the key and stores associated ids!
            Object.entries(transformed).forEach(([id, product]) => {});

            const sortedTransformed = {};
            Object.keys(transformed)
                .sort()
                .forEach((key) => {
                    sortedTransformed[key] = transformed[key];
                });

            this.productData = sortedTransformed;
            this.productsJsonFileTime.lastProcessed =
                this.productsJsonFileTime.request;

            // create a quick access map for fsTags per productName
            const entries = [];
            for (const key in sortedTransformed) {
                const item = sortedTransformed[key];
                const product = item.fs_infos?.product;
                if (product) {
                    entries.push([product, item.fs_tags?.[0] || ""]);
                }
            }
            this.productNameTagMap = Object.fromEntries(entries);

            // in the very end, declare products as processed
            this.productDataProcessed = true;

            // console.log("done with processProductData");
            // console.timeEnd("processProductData");
        },
        async hardRefresh() {
            // Set timestamp first to prevent a refresh loop.
            this.lastSeenRefreshTimestamp = FORCE_REFRESH_TIMESTAMP;

            // 1. Clear IndexedDB
            await db.delete();
            console.log("IndexedDB database deleted.");
            await db.open();
            console.log("IndexedDB database re-initialized.");

            // 2. Reset the state to its initial values, preserving auth and curShop.
            const freshState = JSON.parse(JSON.stringify(defaultState));
            delete freshState.auth;
            delete freshState.curShop;
            delete freshState.lastSeenRefreshTimestamp; // Don't reset the timestamp we just set.

            this.$patch(freshState);

            console.log(
                "State cleared (except auth and curShop). Page will now reload.",
            );
            window.location.assign("/");
        },

        async fetchAndProcessSingleOrder(orderId) {
            console.log(`Fetching and processing single order: ${orderId}`);
            const shop = this.curShop;

            if (!this.productData) {
                await this.fetchProducts();
                this.processProductData();
            }

            // 1. Check IndexedDB first
            const dbOrders = await db.orders
                .where({ id: orderId, shopUrl: shop.url })
                .toArray();

            if (dbOrders.length > 0) {
                console.log(`Order ${orderId} found in cache.`);
                // Combine equipment from all parts of the order
                const combinedEquipment = {};
                for (const orderPart of dbOrders) {
                    if (orderPart.equipmentAmounts) {
                        for (const [key, value] of Object.entries(
                            orderPart.equipmentAmounts,
                        )) {
                            combinedEquipment[key] =
                                (combinedEquipment[key] || 0) + value;
                        }
                    }
                }
                // Return the first part with combined equipment
                return { ...dbOrders[0], equipmentAmounts: combinedEquipment };
            }

            // 2. If not in cache, fetch from API
            console.log(`Order ${orderId} not in cache, fetching from API.`);
            const url = `https://${shop.url}/wp-json/wc/v3/orders/${orderId}`;
            let rawOrder;
            try {
                const response = await fetch(url, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${this.auth.jwts[shop.url]}`,
                    },
                });
                if (!response.ok) {
                    throw new Error(
                        `Failed to fetch order ${orderId}: ${response.statusText}`,
                    );
                }
                rawOrder = await response.json();
            } catch (error) {
                console.error("Error fetching single order:", error);
                const { $tracker: tracker } = useNuxtApp();
                if (tracker) tracker.handleError(error);
                return null;
            }

            // 3. Process the raw order data (reusing existing logic)
            const { getDeliveryNameFromOrder } = useOrder();
            const ordersToStoreInDB = [];
            const shippingMethod = getDeliveryNameFromOrder(rawOrder);

            let date;
            if (shippingMethod == "Lieferung") {
                date = rawOrder.meta_data.find(
                    (m) => m.key == "delivery_date",
                )?.value;
            } else {
                date = rawOrder.meta_data.find(
                    (m) => m.key == "pickup_date",
                )?.value;
            }

            const warenkoerbe = {};
            for (const meta of rawOrder.meta_data) {
                if (meta.key.startsWith("warenkoerbe_")) {
                    const key_match = meta.key.match(
                        /^warenkoerbe_(\d+)_(.*)$/,
                    );
                    if (key_match) {
                        const num = key_match[1];
                        const key = key_match[2];
                        if (!warenkoerbe.hasOwnProperty(num))
                            warenkoerbe[num] = {};
                        warenkoerbe[num][key] = meta.value;
                    }
                }
            }

            if (Object.keys(warenkoerbe).length > 0) {
                for (const [idx, warenkorb] of Object.entries(warenkoerbe)) {
                    const additionalEntry = {};
                    this.createOrderEntry(
                        rawOrder,
                        additionalEntry,
                        shippingMethod,
                        warenkorb,
                    );
                    ordersToStoreInDB.push({
                        ...additionalEntry,
                        shopUrl: shop.url,
                        date: date,
                    });
                }
            }

            const orderEntry = {};
            this.createOrderEntry(rawOrder, orderEntry, shippingMethod, null);
            if (orderEntry.line_items.length > 0) {
                ordersToStoreInDB.push({
                    ...orderEntry,
                    shopUrl: shop.url,
                    date: date,
                });
            }

            // 4. Save to IndexedDB
            if (ordersToStoreInDB.length > 0) {
                try {
                    const plainOrders = JSON.parse(
                        JSON.stringify(ordersToStoreInDB),
                    );
                    await db.orders.bulkPut(plainOrders);
                    console.log(
                        `Order ${orderId} (and its parts) saved to IndexedDB.`,
                    );
                } catch (error) {
                    console.error(
                        "Failed to save single order to IndexedDB",
                        error,
                    );
                    const { $tracker: tracker } = useNuxtApp();
                    if (tracker) tracker.handleError(error);
                }
            }

            // 5. Return composite object
            if (ordersToStoreInDB.length > 0) {
                const combinedEquipment = {};
                for (const orderPart of ordersToStoreInDB) {
                    if (orderPart.equipmentAmounts) {
                        for (const [key, value] of Object.entries(
                            orderPart.equipmentAmounts,
                        )) {
                            combinedEquipment[key] =
                                (combinedEquipment[key] || 0) + value;
                        }
                    }
                }
                return {
                    ...ordersToStoreInDB[0],
                    equipmentAmounts: combinedEquipment,
                };
            }

            return null;
        },

        // Generate OwnTracks configuration URL
        generateOwnTracksConfigUrl(
            username: string,
            deviceId: string,
            monitoringMode = 0,
        ) {
            const config = {
                _type: "configuration",
                mode: 3, // HTTP mode
                url: `https://daily-planner.refueat.dev.ensolarado.de/owntracks`,
                auth: true,
                username: username,
                password: this.essentials?.apis?.daily_planner?.key || "",
                deviceId: deviceId,
                tid: deviceId.substring(0, 2).toUpperCase(), // Use first 2 chars as tracker ID
                httpHeaders: `X-API-Key:${this.essentials?.apis?.daily_planner?.key || ""}`,
                monitoring: monitoringMode, // -1 = Quite mode, 2 = Move mode
                locatorInterval: 60, // Report every 5 minutes
                extendedData: true,
                ignoreInaccurateLocations: 100, // Ignore locations with accuracy > 100m
                positions: 100, // Keep last 1000 positions
            };

            // Convert to JSON and then base64 encode
            const configJson = JSON.stringify(config);
            const base64Config = btoa(configJson);

            // Return the OwnTracks configuration URL
            return `owntracks:///config?inline=${base64Config}`;
        },
    },
});
